import {Observable, Subject, EMPTY, of, throwError} from 'rxjs';
import {Injectable} from "@angular/core";
import {environment} from "../../environments/environment";
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import {Chat} from "../models/chat";
import {Player} from "../models/player";
import { map, tap, expand, reduce, catchError } from "rxjs/operators";
import { AnalyticsService } from "./analytics.service";
import { Message } from "../models/message";
import { TranslateService } from "@ngx-translate/core";
import { PlayerService } from "./player.service";

interface ChatThreadDto {
  chat: ChatSummaryDto;
  participants: ChatParticipantDto[];
}

interface ChatThreadPageDto {
  items: ChatThreadDto[];
  nextBeforeActivity: number;
  hasMore: boolean;
}

interface ChatSummaryDto {
  id: string;
  name: string;
  displayName: string;
  entityId: string;
  entityName: string;
  entityType: string;
  createdAt: number;
  lastMessageAt: number;
  lastMessage: ChatMessageDto;
  managerAnnouncement?: ChatMessageDto;
  lastReadAt: number;
  lastReadMessageCreated: number;
  unreadCount: number;
  hasUnread: boolean;
  priority: number;
  muted: boolean;
  manageable: boolean;
  footballerBotEnabled: boolean;
}

interface ChatParticipantDto {
  contactActions?: Player["contactActions"];
  playerId: string;
  name: string;
  nickName: string;
  avatar: string;
  contact: boolean;
  currentPlayer: boolean;
}

interface ChatMessageDto {
  id: string;
  clientMessageId?: string;
  chatId: string;
  text: string;
  createdAt: number;
  createdDateString: string;
  sender: ChatParticipantDto;
  inappropriate: boolean;
  deleted: boolean;
  likesCount: number;
  dislikesCount: number;
}

@Injectable()
export class ChatService {

  public readonly changed$ = new Subject<void>();

  public apiUrl: string = environment.API_BASE_URL + '/chat';

  constructor(private authHttp: HttpClient, private analytics: AnalyticsService, private translate: TranslateService) {
  }

  public static getRequestOptions() {
    return {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      })
    };
  }

  public getChatForEntityId(entityId: string): Observable<Chat> {
    return this.authHttp.get<ChatThreadDto>(`${environment.API_BASE_URL}/chats/entity/${entityId}`, ChatService.getRequestOptions()).pipe(
      map((thread) => this.toChat(thread)),
      tap((chat) => this.trackChatOpened(chat, entityId))
    );
  }

  // An invite grants match access before chat membership. Do not reject the match itself.
  public getOptionalMatchChat(chatId: string): Observable<Chat> {
    return this.getChatById(chatId, false).pipe(catchError(error =>
      [403, 404].includes(error?.status) ? of(null) : throwError(() => error)));
  }

  public getChatById(chatId: string, trackOpened = true): Observable<Chat> {
    return this.authHttp.get<ChatThreadDto>(`${environment.API_BASE_URL}/chats/${chatId}`, ChatService.getRequestOptions()).pipe(
      map((thread) => this.toChat(thread)),
      tap((chat) => { if (trackOpened) this.trackChatOpened(chat, chatId); })
    );
  }

  public sendManagerAnnouncement(chatId: string, text: string): Observable<Message> {
    return this.authHttp.post<ChatMessageDto>(`${environment.API_BASE_URL}/chats/${chatId}/manager-announcement`, {text}).pipe(map(message => this.toMessage(message)));
  }

  public removeManagerAnnouncement(chatId: string): Observable<void> {
    return this.authHttp.delete<void>(`${environment.API_BASE_URL}/chats/${chatId}/manager-announcement`);
  }

  public applyRealtimeUpdate(chat: Chat, type: string, data: any, playerId: string): Chat {
    if (type === 'manager.announcement.changed') {
      return Object.assign(new Chat(), chat, {managerAnnouncement: data.managerAnnouncement ? this.toMessage(data.managerAnnouncement) : null});
    }
    const message = this.toMessage(data.message);
    const known = chat.messages?.some(item => item.id === message.id);
    const messages = [message, ...(chat.messages || []).filter(item => item.id !== message.id)]
      .sort((left, right) => Number(right.created) - Number(left.created)).slice(0, 5);
    const unreadCount = (chat.unreadCount || 0) + (!known && message.sender?.id !== playerId ? 1 : 0);
    return Object.assign(new Chat(), chat, {messages, unreadCount, hasNewMessages: unreadCount > 0,
      lastMessageDate: String(messages[0]?.created || chat.lastMessageDate)});
  }

  public getChatsForPlayer(playerId: string): Observable<Chat[]> {
    return this.authHttp.get<ChatThreadDto[]>(`${environment.API_BASE_URL}/chats`, ChatService.getRequestOptions()).pipe(
      map((threads) => (threads || []).map((thread) => this.toChat(thread)))
    );
  }

  public getChatPage(beforeActivity?: number, limit: number = 30): Observable<{items: Chat[], nextBeforeActivity: number, hasMore: boolean}> {
    let params = new HttpParams().set("limit", String(limit));
    if (beforeActivity) {
      params = params.set("beforeActivity", String(beforeActivity));
    }
    const options = {
      ...ChatService.getRequestOptions(),
      params
    };
    return this.authHttp.get<ChatThreadPageDto>(`${environment.API_BASE_URL}/chats/page`, options).pipe(
      map((page) => ({
        items: ((page && page.items) || []).map((thread) => this.toChat(thread)),
        nextBeforeActivity: page ? page.nextBeforeActivity : null,
        hasMore: !!(page && page.hasMore)
      }))
    );
  }

  public getChatWindow(minimumItems: number = 30): Observable<{items: Chat[], nextBeforeActivity: number, hasMore: boolean}> {
    let count = 0;
    const cursors = new Set<number>();
    return this.getChatPage(null, 100).pipe(
      expand(page => {
        count += page.items.length;
        if (!page.hasMore || count >= minimumItems || !page.nextBeforeActivity || cursors.has(page.nextBeforeActivity)) return EMPTY;
        cursors.add(page.nextBeforeActivity);
        return this.getChatPage(page.nextBeforeActivity, 100);
      }),
      reduce((result, page) => ({items: [...result.items, ...page.items], nextBeforeActivity: page.nextBeforeActivity, hasMore: page.hasMore}),
        {items: [] as Chat[], nextBeforeActivity: null, hasMore: false})
    );
  }

  public leaveChat(chat: Chat, player: Player): Observable<string> {
    return this.authHttp.put<string>(`${environment.API_BASE_URL}/chats/${chat.id}/leave`, null, ChatService.getRequestOptions());
  }

  public removeChatMember(chatId: string, playerId: string): Observable<Chat> {
    return this.authHttp.delete<ChatThreadDto>(
      `${environment.API_BASE_URL}/chats/${chatId}/players/${playerId}`,
      ChatService.getRequestOptions()).pipe(
      map((thread) => this.toChat(thread))
    );
  }

  public addChatMember(chatId: string, playerId: string): Observable<Chat> {
    return this.authHttp.post<ChatThreadDto>(
      `${environment.API_BASE_URL}/chats/${chatId}/players/${playerId}`,
      null,
      ChatService.getRequestOptions()).pipe(
      map((thread) => this.toChat(thread))
    );
  }

  public createChat(body: Object): Observable<Chat> {
    const chat = body as Chat;
    const request = {
      name: chat.displayName || chat.name,
      playerIds: (chat.players || []).map((player) => player.id).filter((playerId) => !!playerId)
    };
    return this.authHttp.post<ChatThreadDto>(
      `${environment.API_BASE_URL}/chats`,
      JSON.stringify(request),
      ChatService.getRequestOptions()).pipe(
      map((thread) => this.toChat(thread))
    );
  }

  public openDirectChat(playerId: string): Observable<{ chat: Chat }> {
    return this.authHttp.post<ChatThreadDto>(
      `${environment.API_BASE_URL}/chats/direct/${playerId}`,
      null,
      ChatService.getRequestOptions()).pipe(
      map((thread) => ({chat: this.toChat(thread)}))
    );
  }

  public markRead(chatId: string): Observable<Chat> {
    return this.authHttp.put<ChatThreadDto>(
      `${environment.API_BASE_URL}/chats/${chatId}/read`,
      null,
      ChatService.getRequestOptions()).pipe(
      map((thread) => this.toChat(thread)),
      tap(() => this.changed$.next())
    );
  }

  public setFootballerBotEnabled(chatId: string, enabled: boolean): Observable<Chat> {
    return this.authHttp.put<ChatThreadDto>(
      `${environment.API_BASE_URL}/chats/${chatId}/footballer-bot`,
      JSON.stringify({enabled}),
      ChatService.getRequestOptions()).pipe(
      map((thread) => this.toChat(thread))
    );
  }

  public updateChat(body: Object): Observable<Chat> {
    const chat = body as Chat;
    const request = {
      name: chat.displayName || chat.name,
      playerIds: (chat.players || []).map((player) => player.id).filter((playerId) => !!playerId)
    };
    return this.authHttp.put<ChatThreadDto>(
      `${environment.API_BASE_URL}/chats/${chat.id}`,
      JSON.stringify(request),
      ChatService.getRequestOptions()).pipe(
      map((thread) => this.toChat(thread))
    );
  }

  public removeChat(id: string): Observable<Chat> {
    return this.authHttp.delete<Chat>(`${environment.API_BASE_URL}/chats/${id}`);
  }

  public getPlayers(chatId: string): Observable<Player[]> {
    return this.getChatById(chatId).pipe(
      map((chat) => chat.players || [])
    );
  }

  public calculateDisplayName(chat: Chat, currentPlayerId: string) : Chat {
    if(chat.entityType == "EVENT"){
      chat.displayName = chat.name;
    }else if(chat.entityType == "PLAYER"){
      if(chat.players.length == 2){
        chat.players.forEach(player => {
          if(currentPlayerId != player.id){
            chat.displayName = player.nickName;
          }              
        });
      }else if(chat.players.length == 1){
        chat.displayName = this.translate.instant('chat.playerLeft');
      }else if(chat.players.length > 2){
        chat.displayName = chat.name;
      }
    }
    return chat;
  }

  private trackChatOpened(chat: Chat, fallbackId: string): void {
    this.analytics.track("chat_opened", {
      screen: "chat",
      entityType: chat && chat.entityType ? chat.entityType.toLowerCase() : "chat",
      entityId: chat && chat.entityId ? chat.entityId : fallbackId
    });
  }

  private toChat(thread: ChatThreadDto): Chat {
    const chat = new Chat();
    if (!thread || !thread.chat) {
      return chat;
    }
    chat.id = thread.chat.id;
    chat.name = thread.chat.name;
    chat.displayName = thread.chat.displayName;
    chat.entityId = thread.chat.entityId;
    chat.entityName = thread.chat.entityName;
    chat.entityType = thread.chat.entityType;
    chat.created = thread.chat.createdAt ? new Date(thread.chat.createdAt) : null;
    chat.lastMessageDate = thread.chat.lastMessageAt ? String(thread.chat.lastMessageAt) : null;
    chat.priority = thread.chat.priority;
    chat.lastReadAt = thread.chat.lastReadAt;
    chat.lastReadMessageCreated = thread.chat.lastReadMessageCreated;
    chat.unreadCount = thread.chat.unreadCount || 0;
    chat.hasNewMessages = !!thread.chat.hasUnread;
    chat.footballerBotEnabled = thread.chat.footballerBotEnabled !== false;
    chat.players = (thread.participants || []).map((participant) => this.toPlayer(participant));
    const contact = (thread.participants || []).find((participant) => participant.contact);
    chat.contact = this.toPlayer(contact);
    chat.mutedPlayers = thread.chat.muted ? [this.currentParticipantId(thread)] : [];
    chat.managerAnnouncement = thread.chat.managerAnnouncement ? this.toMessage(thread.chat.managerAnnouncement) : null;
    chat.messages = thread.chat.lastMessage ? [this.toMessage(thread.chat.lastMessage)] : [];
    (chat as any).lastMessage = thread.chat.lastMessage ? thread.chat.lastMessage.text : this.translate.instant('chat.noMessages');
    return chat;
  }

  private currentParticipantId(thread: ChatThreadDto): string {
    const current = (thread.participants || []).find((participant) => participant.currentPlayer);
    return current ? current.playerId : null;
  }

  private toPlayer(dto: ChatParticipantDto): Player {
    const player = new Player();
    if (!dto) {
      return player;
    }
    player.id = dto.playerId;
    player.name = dto.name;
    player.nickName = dto.nickName;
    player.avatar = dto.avatar;
    player.contactActions = dto.contactActions;
    return PlayerService.normalizePlayerAvatar(player);
  }

  private toMessage(dto: ChatMessageDto): Message {
    const message = new Message();
    message.id = dto.id;
    message.clientMessageId = dto.clientMessageId;
    message.chatId = dto.chatId;
    message.text = dto.text;
    message.sender = this.toPlayer(dto.sender);
    (message as any).created = dto.createdAt;
    message.createdDateString = dto.createdDateString;
    message.likesCount = dto.likesCount || 0;
    message.dislikesCount = dto.dislikesCount || 0;
    message.inappropriate = !!dto.inappropriate;
    message.deleted = !!dto.deleted;
    return message;
  }
}
